const BAR_FIELDS = ['t', 'o', 'h', 'l', 'c', 'v']

export function emptyBars() {
  return {
    t: [], o: [], h: [], l: [], c: [], v: [],
  }
}

// The bars of a history answer, or null when the feed has none there. Anything else that is
// not `ok` with six arrays of one length is an error.
export function barsFromHistory(body) {
  if (!body || typeof body !== 'object') throw new Error('The history answer is not an object')
  if (body.s === 'no_data') return null
  if (body.s !== 'ok') throw new Error(`The history answer has status ${String(body.s)}`)
  if (BAR_FIELDS.some(key => !Array.isArray(body[key]))) throw new Error('The history answer lacks a bar array')

  const { length } = body.t
  if (BAR_FIELDS.some(key => body[key].length !== length)) throw new Error('The history arrays differ in length')

  return length ? body : null
}

export function mergeRawData(existing, incoming, resetData) {
  if (!incoming) return existing
  if (resetData || !existing.t || !existing.t.length) return incoming

  // An older window can reach into the bars held: only what comes before them is added,
  // since a time drawn twice breaks the chart
  const first = existing.t[0]
  let count = 0
  while (count < incoming.t.length && incoming.t[count] < first) count += 1

  const merged = { ...existing }
  BAR_FIELDS.forEach(key => {
    merged[key] = [...incoming[key].slice(0, count), ...existing[key]]
  })

  return merged
}

// Monday 00:00 UTC of the week containing t (epoch day 0 is a Thursday)
export function weekStart(t) {
  const day = Math.floor(t / 86400)

  return (day - ((day + 3) % 7)) * 86400
}

// The history API has no weekly resolution, so weekly bars are built from daily ones
export function aggregateWeekly(raw) {
  if (!raw) return raw

  const {
    t = [], o = [], h = [], l = [], c = [], v = [],
  } = raw
  const weekly = {
    t: [], o: [], h: [], l: [], c: [], v: [],
  }

  t.forEach((time, idx) => {
    const week = weekStart(time)
    const last = weekly.t.length - 1

    if (weekly.t[last] !== week) {
      weekly.t.push(week)
      weekly.o.push(o[idx])
      weekly.h.push(h[idx])
      weekly.l.push(l[idx])
      weekly.c.push(c[idx])
      weekly.v.push(v[idx])

      return
    }

    weekly.h[last] = Math.max(weekly.h[last], h[idx])
    weekly.l[last] = Math.min(weekly.l[last], l[idx])
    weekly.c[last] = c[idx]
    weekly.v[last] += v[idx]
  })

  return { ...raw, ...weekly }
}

function numberOf(value) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const number = Number(value)

    return Number.isFinite(number) ? number : null
  }

  return null
}

// The only function that knows a realtime payload's field names; null when it has no symbol,
// or no numeric time and close
export function parseTick(payload) {
  if (!payload || typeof payload !== 'object') return null
  if (typeof payload.symbol !== 'string' || payload.symbol === '') return null

  const time = numberOf(payload.t)
  const close = numberOf(payload.c)
  if (time === null || close === null) return null

  return {
    symbol: payload.symbol,
    time,
    open: numberOf(payload.o) ?? close,
    high: numberOf(payload.h) ?? close,
    low: numberOf(payload.l) ?? close,
    close,
    volume: numberOf(payload.v) ?? 0,
  }
}

// The start of the bar a moment falls in
export function barTime(time, resolution) {
  if (resolution.includes('W')) return weekStart(time)

  const size = (resolution.includes('D') ? 1440 : Number(resolution)) * 60

  return Math.floor(time / size) * size
}

// The bars with a tick applied, or null when it changes nothing. The bar is found by its time,
// so it does not depend on an index kept for another symbol or resolution.
export function applyTick(rawData, input, resolution) {
  const tick = parseTick(input)
  const count = rawData && rawData.t ? rawData.t.length : 0
  if (!tick || !count) return null

  const time = barTime(tick.time, resolution)
  const isNew = time > rawData.t[count - 1]
  const idx = isNew ? count : rawData.t.lastIndexOf(time)
  // A tick for an earlier bar that is not held
  if (idx === -1) return null

  const dt = { ...rawData }
  BAR_FIELDS.forEach(key => {
    dt[key] = [...rawData[key]]
  })

  if (isNew) {
    dt.t.push(time)
    dt.o.push(tick.open)
    dt.h.push(tick.high)
    dt.l.push(tick.low)
    dt.c.push(tick.close)
    dt.v.push(tick.volume)

    return dt
  }

  // By the close alone: what period a tick's own high and low cover is not known
  dt.h[idx] = Math.max(dt.h[idx], tick.close)
  dt.l[idx] = Math.min(dt.l[idx], tick.close)
  dt.c[idx] = tick.close
  dt.v[idx] += tick.volume

  return dt
}
